import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type {
  AdminAgentFamily,
  AdminAgentInstance,
} from "@/queries/adminAgents";
import { useUpdateAgentFamily } from "@/queries/adminAgents";

import { aggregateEnabledBudget } from "./utils";

// Threshold above which the dialog requires explicit confirmation
// before save. ADR-0013 says "Save requires confirmation if changes
// affect ≥3 instances".
const CONFIRM_THRESHOLD = 3;

interface FamilyEditDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  family: AdminAgentFamily;
  instances: AdminAgentInstance[];
}

export function FamilyEditDialog({
  open,
  onOpenChange,
  family,
  instances,
}: FamilyEditDialogProps) {
  const { t } = useTranslation("admin-agents");
  const mutation = useUpdateAgentFamily(family.skill);

  // Working copy of the defaults dict, edited in place. Each input writes
  // back as a string and we coerce on save based on the field name.
  const [draft, setDraft] = useState<Record<string, string>>(() =>
    toEditableStrings(family.defaults, family.allowed_keys),
  );
  const [confirmed, setConfirmed] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const affected = useMemo(
    () => instances.filter((i) => i.skill === family.skill).length,
    [instances, family.skill],
  );

  const budgetFrom = family.total_daily_budget_usd;
  const budgetTo = useMemo(() => {
    const merged = mergeDraftIntoDefaults(family.defaults, draft, family.allowed_keys);
    return aggregateEnabledBudget(instances, family.skill, merged);
  }, [draft, family, instances]);

  const requiresConfirm = affected >= CONFIRM_THRESHOLD;
  const canSave = !requiresConfirm || confirmed;

  const handleSave = () => {
    setSubmitError(null);
    const payload = mergeDraftIntoDefaults(family.defaults, draft, family.allowed_keys);
    mutation.mutate(
      { defaults: payload },
      {
        onSuccess: () => onOpenChange(false),
        onError: (err) => setSubmitError(err.message || t("errors.saveGeneric")),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t("dialogs.familyEdit.title", { skill: family.skill })}
          </DialogTitle>
          <DialogDescription>{t("dialogs.familyEdit.subtitle")}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          {family.allowed_keys.map((key) => (
            <div key={key} className="flex flex-col gap-1">
              <Label htmlFor={`family-${family.skill}-${key}`}>
                {t(`family.fieldLabels.${key}`, { defaultValue: key })}
              </Label>
              <Input
                id={`family-${family.skill}-${key}`}
                value={draft[key] ?? ""}
                onChange={(e) => setDraft((p) => ({ ...p, [key]: e.target.value }))}
                data-testid={`family-edit-${key}`}
              />
              {hintFor(key, t) ? (
                <p className="text-xs text-muted-foreground">{hintFor(key, t)}</p>
              ) : null}
            </div>
          ))}
        </div>

        <Alert data-testid="family-cascade-preview">
          <AlertDescription>
            <div>
              {affected === 1
                ? t("dialogs.familyEdit.impactOne")
                : t("dialogs.familyEdit.impactOther", { count: affected })}
            </div>
            <div>
              {t("dialogs.familyEdit.budgetChange", { from: budgetFrom, to: budgetTo })}
            </div>
          </AlertDescription>
        </Alert>

        {requiresConfirm ? (
          <label
            className="flex items-start gap-2 text-sm"
            data-testid="family-cascade-confirm-label"
          >
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              data-testid="family-cascade-confirm"
              className="mt-1"
            />
            <span>
              {t("dialogs.familyEdit.confirmCheckbox", { count: affected })}
            </span>
          </label>
        ) : null}

        {submitError ? (
          <Alert variant="destructive">
            <AlertDescription>{submitError}</AlertDescription>
          </Alert>
        ) : null}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t("dialogs.familyEdit.cancel")}
          </Button>
          <Button
            onClick={handleSave}
            disabled={!canSave || mutation.isPending}
            data-testid="family-edit-save"
          >
            {mutation.isPending
              ? t("dialogs.familyEdit.saving")
              : t("dialogs.familyEdit.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────

function toEditableStrings(
  source: Record<string, unknown>,
  keys: readonly string[],
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const k of keys) {
    const v = source[k];
    if (v === undefined || v === null) {
      out[k] = "";
    } else if (Array.isArray(v)) {
      out[k] = v.join(",");
    } else if (typeof v === "object") {
      out[k] = JSON.stringify(v);
    } else {
      out[k] = String(v);
    }
  }
  return out;
}

function mergeDraftIntoDefaults(
  original: Record<string, unknown>,
  draft: Record<string, string>,
  keys: readonly string[],
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const k of keys) {
    const raw = draft[k];
    if (raw === undefined || raw === "") {
      // Field cleared — fall back to original. Server would reject a
      // partial dict that drops a previously-set required key.
      if (k in original) out[k] = original[k];
      continue;
    }
    out[k] = coerce(k, raw);
  }
  return out;
}

function coerce(key: string, raw: string): unknown {
  if (key === "sources") {
    return raw
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
  }
  if (key === "cadence_minutes") {
    const n = Number(raw);
    return Number.isFinite(n) ? Math.trunc(n) : raw;
  }
  // daily_budget_usd / counterfactual_weight — preserve as decimal-string.
  return raw;
}

function hintFor(key: string, t: (k: string) => string): string | null {
  if (key === "cadence_minutes") return t("family.fieldHints.cadence_minutes");
  if (key === "daily_budget_usd") return t("family.fieldHints.daily_budget_usd");
  if (key === "counterfactual_weight") return t("family.fieldHints.counterfactual_weight");
  return null;
}
